<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import PublicHeader from "@/components/public/PublicHeader.vue";
import PublicFooter from "@/components/public/PublicFooter.vue";

const { t } = useI18n();

const router = useRouter();

const handleHelpClick = () => {
  console.log("Help button clicked");
};

const handleLanguageChange = (language: string) => {
  console.log("Language changed to:", language);
};

const handleFooterLinkClick = (section: string, item: string) => {
  console.log("Footer link clicked:", section, item);
};

const handleRegister = () => {
  console.log("Developer registration initiated");
};
</script>

<template>
  <PublicHeader
    @help-click="handleHelpClick"
    @language-change="handleLanguageChange"
  />

  <main id="main-content" class="min-h-screen">
    <section
      id="registration-hero"
      class="bg-gradient-to-r from-primary to-accent text-white h-[200px] flex items-center"
    >
      <div class="max-w-4xl mx-auto px-5 w-full">
        <div class="text-center">
          <h1 class="text-3xl font-bold mb-2">
            {{ $t('developer.register_page.title') }}
          </h1>
          <p class="text-lg text-gray-200">
            {{ $t('developer.register_page.subtitle') }}
          </p>
        </div>
      </div>
    </section>

    <section id="registration-form" class="py-12">
      <div class="max-w-4xl mx-auto px-5">
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div id="form-header" class="border-b border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-2xl font-bold text-accent">
                  {{ $t('developer.register_page.form_title') }}
                </h2>
                <p class="text-gray-600 mt-1">
                  {{ $t('developer.register_page.form_subtitle') }}
                </p>
              </div>
              <div class="text-right">
                <div class="text-sm text-gray-500">{{ $t('developer.register_page.registration_fee') }}</div>
                <div class="text-2xl font-bold text-primary">$1,000.00</div>
              </div>
            </div>
          </div>

          <form id="developer-registration-form" class="p-6 space-y-8">
            <div id="organization-info" class="space-y-6">
              <h3
                class="text-lg font-semibold text-accent border-b border-gray-200 pb-2"
              >
                <i class="fas fa-building text-primary mr-2"></i>{{ $t('developer.register_page.form_fields.organization_info') }}
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.full_name_org') }} *</label
                  >
                  <input
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    :placeholder="$t('developer.register_page.form_fields.full_name_org_placeholder')"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.business_registration_number') }} *</label
                  >
                  <input
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    :placeholder="$t('developer.register_page.form_fields.business_registration_placeholder')"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.legal_status') }} *</label
                  >
                  <select
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option>{{ $t('developer.register_page.form_fields.select_legal_status') }}</option>
                    <option>{{ $t('developer.register_page.form_fields.corporation') }}</option>
                    <option>{{ $t('developer.register_page.form_fields.llc') }}</option>
                    <option>{{ $t('developer.register_page.form_fields.partnership') }}</option>
                    <option>{{ $t('developer.register_page.form_fields.non_profit') }}</option>
                    <option>{{ $t('developer.register_page.form_fields.government_entity') }}</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.register_page.form_fields.country_incorporation') }} *</label
                >
                <select
                  class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>{{ $t('developer.register_page.form_fields.select_country') }}</option>
                  <option>{{ $t('developer.register_page.form_fields.united_states') }}</option>
                  <option>{{ $t('developer.register_page.form_fields.united_kingdom') }}</option>
                  <option>{{ $t('developer.register_page.form_fields.canada') }}</option>
                  <option>{{ $t('developer.register_page.form_fields.germany') }}</option>
                  <option>{{ $t('developer.register_page.form_fields.france') }}</option>
                  <option>{{ $t('developer.register_page.form_fields.other') }}</option>
                </select>
              </div>
            </div>

            <div id="contact-info" class="space-y-6">
              <h3
                class="text-lg font-semibold text-accent border-b border-gray-200 pb-2"
              >
                <i class="fas fa-address-card text-primary mr-2"></i>{{ $t('developer.register_page.form_fields.contact_information') }}
              </h3>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.register_page.form_fields.physical_address') }} *</label
                >
                <textarea
                  class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  :placeholder="$t('developer.register_page.form_fields.physical_address_placeholder')"
                ></textarea>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.contact_email') }} *</label
                  >
                  <input
                    type="email"
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                    :placeholder="$t('developer.register_page.form_fields.contact_email_placeholder')"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.phone_number') }} *</label
                  >
                  <input
                    type="tel"
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                    :placeholder="$t('developer.register_page.form_fields.phone_placeholder')"
                  />
                </div>
              </div>
            </div>

            <div id="representative-info" class="space-y-6">
              <h3
                class="text-lg font-semibold text-accent border-b border-gray-200 pb-2"
              >
                <i class="fas fa-user-tie text-primary mr-2"></i>{{ $t('developer.register_page.form_fields.local_representative') }}
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.representative_name') }}</label
                  >
                  <input
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                    :placeholder="$t('developer.register_page.form_fields.representative_name_placeholder')"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.register_page.form_fields.representative_email') }}</label
                  >
                  <input
                    type="email"
                    class="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                    :placeholder="$t('developer.register_page.form_fields.representative_email_placeholder')"
                  />
                </div>
              </div>
            </div>


            <div id="declarations" class="space-y-6">
              <h3
                class="text-lg font-semibold text-accent border-b border-gray-200 pb-2"
              >
                <i class="fas fa-check-circle text-primary mr-2"></i
                >{{ $t('developer.register_page.form_fields.declarations_terms') }}
              </h3>

              <div class="space-y-4">
                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="accuracy"
                    class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label for="accuracy" class="text-sm text-gray-700"
                    >{{ $t('developer.register_page.form_fields.accuracy_declaration') }}</label
                  >
                </div>

                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="compliance"
                    class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label for="compliance" class="text-sm text-gray-700"
                    >{{ $t('developer.register_page.form_fields.compliance_agreement') }}</label
                  >
                </div>

                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms"
                    class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label for="terms" class="text-sm text-gray-700"
                    >{{ $t('developer.register_page.form_fields.terms_acceptance') }}
                    <span class="text-primary underline cursor-pointer"
                      >{{ $t('developer.register_page.form_fields.terms_service') }}</span
                    >
                    {{ $t('developer.register_page.form_fields.and') }}
                    <span class="text-primary underline cursor-pointer"
                      >{{ $t('developer.register_page.form_fields.privacy_policy') }}</span
                    >.</label
                  >
                </div>

                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="fees"
                    class="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label for="fees" class="text-sm text-gray-700"
                    >{{ $t('developer.register_page.form_fields.fee_acknowledgment') }}</label
                  >
                </div>
              </div>
            </div>

            <div id="form-actions" class="border-t border-gray-200 pt-6">
              <div class="flex flex-col sm:flex-row gap-4 justify-between">
                <button
                  type="button"
                  @click="router.push('/')"
                  class="px-6 py-3 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition-colors"
                >
                  <i class="fas fa-arrow-left mr-2"></i>{{ $t('developer.register_page.form_actions.back_to_portal') }}
                </button>
                <div class="flex gap-4">
                  <button
                    type="button"
                    class="px-6 py-3 border border-primary text-primary rounded-md font-medium hover:bg-primary/5 transition-colors"
                  >
                    {{ $t('developer.register_page.form_actions.save_draft') }}
                  </button>
                  <button
                    type="submit"
                    class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90 transition-colors"
                  >
                    {{ $t('developer.register_page.form_actions.submit_pay') }}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div
          id="registration-info"
          class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 class="font-semibold text-blue-800 mb-3">
              <i class="fas fa-info-circle mr-2"></i>{{ $t('developer.register_page.info_boxes.what_happens_next.title') }}
            </h3>
            <ul class="text-sm text-blue-700 space-y-2">
              <li>• {{ $t('developer.register_page.info_boxes.what_happens_next.review_time') }}</li>
              <li>• {{ $t('developer.register_page.info_boxes.what_happens_next.din_generation') }}</li>
              <li>• {{ $t('developer.register_page.info_boxes.what_happens_next.email_verification') }}</li>
              <li>• {{ $t('developer.register_page.info_boxes.what_happens_next.portal_access') }}</li>
            </ul>
          </div>

          <div class="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 class="font-semibold text-green-800 mb-3">
              <i class="fas fa-shield-alt mr-2"></i>{{ $t('developer.register_page.info_boxes.secure_compliant.title') }}
            </h3>
            <ul class="text-sm text-green-700 space-y-2">
              <li>• {{ $t('developer.register_page.info_boxes.secure_compliant.encryption') }}</li>
              <li>• {{ $t('developer.register_page.info_boxes.secure_compliant.gdpr_compliant') }}</li>
              <li>• {{ $t('developer.register_page.info_boxes.secure_compliant.audit_trail') }}</li>
              <li>• {{ $t('developer.register_page.info_boxes.secure_compliant.security_monitoring') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>

  <PublicFooter @link-click="handleFooterLinkClick" />
</template>
